import { useMemo } from 'react';
import { policyType as PolicyType } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import { awsDateToStringDate } from 'core/helpers/formatters';
import useDetailedPrice from 'offer/hooks/use-detailed-price';
import usePlanPrice from 'offer/hooks/use-plan-price';
import useBillingDayOfMonthOptions from 'core/hooks/use-billing-day-of-month-options';
import { getPrimaryOrBlankMortgageDetails } from 'offer/helpers/mortgage-helpers';
import { autoPaymentModeForMethod, isAutoDirectBillAllowed } from 'core/helpers/auto-direct-bill';
import { seedStressFreeSwitch } from '../../helper';

const useFormInitialValues = (offer, policyType) => {
  const { homeDownPayment, autoDownPayment, rentersDownPayment, condoDownPayment } = usePlanPrice(
    offer,
    policyType,
    useDetailedPrice
  );
  const billingDayOptions = useBillingDayOfMonthOptions(offer, policyType);

  let mortgageDetails = [];

  if (policyType?.includes(PolicyType.Home)) {
    mortgageDetails = offer.quote.home.mortgageDetails;
  } else if (policyType?.includes(PolicyType.Condo)) {
    mortgageDetails = offer.quote.condo.mortgageDetails;
  } else if (isFarmBasedPolicyType(policyType)) {
    mortgageDetails = offer.quote.farm?.mortgageDetails;
  }
  const primaryMortgageDetails = getPrimaryOrBlankMortgageDetails(mortgageDetails);
  return useMemo(() => {
    if (!policyType) {
      return {};
    }

    const isRenters = policyType === PolicyType.ARBundle;
    const { quote } = offer;
    const stressFreeSwitch = seedStressFreeSwitch(quote.global);
    const { email, phone, additionalPhoneNumbers, interestedParties, farmInterestedParties, lienHolders } = quote;
    const {
      autoEffectiveDate,
      autoPaymentMethod,
      autoPaymentType,
      autoPaymentMode,
      homeEffectiveDate,
      homeownersPaymentType,
      homeownersPaymentMethod,
      homeMegaDownPay,
      billingDayOfMonth: globalBillingDayOfMonth,
      homeBillingDayOfMonth,
      autoBillingDayOfMonth,
      autoMegaDownPay,
      currentAutoCarrierPolicyNumber,
      currentAutoCarrier,
      currentHomeownersCarrier,
      rentersBillingDayOfMonth,
      rentersEffectiveDate,
      rentersPaymentType,
      rentersPaymentMethod,
      rentersMegaDownPay,
      condoBillingDayOfMonth,
      condoEffectiveDate,
      condoPaymentType,
      condoPaymentMethod,
      condoMegaDownPay,
      directBillPreferredPaymentMethod,
      farmBillingDayOfMonth,
      farmEffectiveDate,
      farmPaymentType,
      farmPaymentMethod,
      farmPaymentMode,
      farmDownPayment,
      noBindHome,
      noBindAuto
    } = quote.global;

    const billingDayOfMonth = globalBillingDayOfMonth || billingDayOptions?.pop()?.id; // default to the last option
    // BillToOther parties for Farm policies live in `farmInterestedParties` (the farm-specific
    // array); auto's (CFM, #36200) live with the auto interested parties in `lienHolders`. The general
    // `interestedParties` array is used by the other policy types.
    let billToOtherParties = interestedParties;
    if (isFarmBasedPolicyType(policyType)) {
      billToOtherParties = farmInterestedParties;
    } else if (policyType === PolicyType.Auto) {
      billToOtherParties = lienHolders;
    }
    const billToOtherParty = billToOtherParties?.find((party) => party.relationship === 'billToOther');

    return {
      billingDayOfMonth,
      address: isRenters ? offer.quote.rentersCoverage.rentersLocation : offer.quote.correctedAddress,
      firstName: offer.quote.fname,
      lastName: offer.quote.lname,
      // Always the policyholder's email — used for the member's Stripe customer. The BillToOther
      // party's email is tracked separately on `billToOtherParty.email` and used for the
      // BillToOther Stripe customer (the one that gets charged at bind).
      email,
      phone,
      additionalPhoneNumbers,
      cardBrand: '',
      cardLast4: '',
      completeCardData: false,
      isRoutingNumberValid: false,
      isAccountNumberValid: false,
      isAccountHolderNameValid: false,
      breakupESignature: false,
      basisTheoryCardToken: '',
      basisTheoryBankAccountToken: '',
      // Underwriter-only opt-in (issue #33938) to skip MicroBilt verification on the ACH bind.
      bypassMicroBiltVerification: false,
      // Underwriter-only opt-in (issue #33891) to issue without collecting payment details at bind.
      collectPaymentLater: false,
      selectedOption: policyType,
      noBindHome,
      noBindAuto,
      noAptNumber: false,
      billToOtherParty,

      // Auto
      autoEffectiveDate: awsDateToStringDate(autoEffectiveDate),
      autoBillingDayOfMonth: autoBillingDayOfMonth || billingDayOfMonth,
      autoPaymentType,
      autoPaymentMethod,
      // On brands that direct-bill auto the mode follows the method (#36200), so a mode saved before the
      // method last changed can't disagree with it.
      autoPaymentMode: isAutoDirectBillAllowed() ? autoPaymentModeForMethod(autoPaymentMethod) : autoPaymentMode || 'A',
      autoDownPayment,
      autoMegaDownPay,
      attestationsAutoAccepted: null,
      breakupWithAuto: stressFreeSwitch.breakupWithAuto,
      currentAutoCarrier: currentAutoCarrier || '',
      currentAutoCarrierPolicyNumber: currentAutoCarrierPolicyNumber || '',
      drivers: [],
      cars: policyType.includes(PolicyType.Auto) ? offer.quote.cars : [],

      // Home
      homeBillingDayOfMonth: homeBillingDayOfMonth || billingDayOfMonth,
      homeEffectiveDate: awsDateToStringDate(homeEffectiveDate),
      homeownersPaymentType,
      homeownersPaymentMethod,
      homeMegaDownPay,
      homeDownPayment,
      attestationsHomeAccepted: null,
      mortgageDetails: primaryMortgageDetails,
      loanOfficerEmail: '',
      breakupWithHomeowners: stressFreeSwitch.breakupWithHomeowners,
      currentHomeownersCarrier: currentHomeownersCarrier || '',
      currentHomeownersCarrierPolicyNumber: '',

      // Renters
      rentersBillingDayOfMonth: rentersBillingDayOfMonth || billingDayOfMonth,
      rentersEffectiveDate: awsDateToStringDate(rentersEffectiveDate),
      rentersPaymentType,
      rentersPaymentMethod,
      rentersDownPayment,
      rentersMegaDownPay,
      attestationsRentersAccepted: null,

      // Condo
      condoBillingDayOfMonth: condoBillingDayOfMonth || billingDayOfMonth,
      condoEffectiveDate: awsDateToStringDate(condoEffectiveDate),
      condoPaymentType,
      condoPaymentMethod,
      condoDownPayment,
      condoMegaDownPay,
      attestationsCondoAccepted: null,

      // Farm
      directBillPreferredPaymentMethod,
      farmBillingDayOfMonth: farmBillingDayOfMonth || billingDayOfMonth,
      farmEffectiveDate: awsDateToStringDate(farmEffectiveDate),
      farmPaymentType,
      farmPaymentMethod,
      farmPaymentMode,
      farmDownPayment,

      breakupWithRenters: stressFreeSwitch.breakupWithRenters,
      currentRentersCarrier: currentHomeownersCarrier || '',
      currentRentersCarrierPolicyNumber: '',

      // Payment disclosures
      authorizePayments: false,

      // Pre-Sale Checklist
      homeInspectionInformed: false,
      buyingCondoPolicyInformed: false,
      inColoradoInformed: false,
      branchCommunicationConsent: false,
      allApplicantsConfirmed: false,
      priorInsuranceEntered: false,
      homePaymentRemindersUpdated: false,
      autoPaymentRemindersUpdated: false,
      confirmDiscounts: false,
      confirmCoverages: false,
      noWindHailInformed: false,
      earthquakeAndFloodInformed: false,
      proofOfPriorCoverageInformed: false,
      lapsedLicensesVerified: false,
      excludedDriversInformed: false,
      roofSurfacesEndorsementInformed: false,
      driversLicenseInformed: false,
      michiganPIPInformed: false,
      trailingDocsInformed: false,
      homeAgeInventoryDiscountInformed: false,
      hasFortifiedHome: false,

      // Extra questions
      opposingPolicySoldByAgency: null,
      optIntoMarketingCampaigns: offer.quote.optIntoMarketingCampaigns || false,

      usingNonVerifiedData: offer.usingNonVerifiedData,
      /**
       * This node is populated with data when the staff user enters checkout payment info
       * but doesn't complete the purchase (usually because the customer asks for more offer modifications)
       * because they navigated back to the MQFS route
       */
      recoveredPaymentData: null
    };
  }, [
    offer,
    policyType,
    billingDayOptions,
    primaryMortgageDetails,
    autoDownPayment,
    condoDownPayment,
    homeDownPayment,
    rentersDownPayment
  ]);
};

export default useFormInitialValues;
